import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FamilyBranding } from '../models/branding.model';

export const CATAN_SUBDOMAIN = 'catan';

/**
 * Sinkrona provjera hosta.
 *
 * Rute se razrjesavaju prije nego SubdomainService.init() stigne popuniti signal,
 * pa redirect ne smije ovisiti o signalu.
 */
export function isCatanHost(): boolean {
  return window.location.hostname.split('.')[0] === CATAN_SUBDOMAIN;
}

/**
 * Apsolutni URL Catan subdomene, izveden iz trenutnog hosta.
 *
 * Catan živi na vlastitoj subdomeni pa routerLink ne dolazi u obzir. Bazna domena
 * se dobiva odbacivanjem obiteljske subdomene ako je ima:
 *   my-famil.io          → catan.my-famil.io
 *   obitelj.my-famil.io  → catan.my-famil.io
 *   my-familio.local     → catan.my-familio.local
 *
 * Vraća null na hostu bez točke (localhost, IP) — ondje subdomene ne postoje.
 */
export function catanUrl(): string | null {
  const { hostname, protocol, port } = window.location;
  const labels = hostname.split('.');

  if (labels.length < 2) {
    return null;
  }

  const baseDomain = labels.length > 2 ? labels.slice(1).join('.') : hostname;
  const suffix = port ? `:${port}` : '';

  return `${protocol}//${CATAN_SUBDOMAIN}.${baseDomain}${suffix}`;
}

@Injectable({ providedIn: 'root' })
export class SubdomainService {
  private http = inject(HttpClient);

  subdomain = signal<string | null>(null);
  familyBranding = signal<FamilyBranding | null>(null);

  /** catan.my-famil.io je zaseban proizvod, ne obiteljska subdomena. */
  readonly isCatan = computed(() => this.subdomain() === CATAN_SUBDOMAIN);

  init(): void {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    if (parts.length < 3) {
      return;
    }

    const sub = parts[0];
    this.subdomain.set(sub);

    // Catan nema obitelj iza sebe — /api/subdomain/branding bi vratio 404.
    if (sub === CATAN_SUBDOMAIN) {
      return;
    }

    this.loadBranding();
  }

  private loadBranding(): void {
    this.http.get<FamilyBranding>('/api/subdomain/branding').subscribe({
      next: branding => this.familyBranding.set(branding),
      error: () => this.familyBranding.set(null),
    });
  }
}
